// 005 FR-026, US3-2: "Ordering for" — the parent's linked children as choices, unlinked ones as
// "Not linked" leading to the link flow, and Add student. On behalf, the context's banner instead.
import { useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import { selectStudent } from "@/profile/selection";
import { canAddChild } from "@/profile/viewModels";
import { makeStyles } from "@/theme/makeStyles";
import { InlineMessage } from "@/ui/InlineMessage";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { SHOP_COPY } from "./copy";
import { useShopping } from "./shopApi";
import { classSectionText, useShopper } from "./useShopper";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    box: { gap: t.space.xs },
    chips: { flexDirection: "row", flexWrap: "wrap", gap: t.space.sm },
    chip: {
      minHeight: t.size.button,
      paddingHorizontal: t.space.md,
      borderRadius: t.radius.button,
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
      justifyContent: "center",
    },
    chipOn: { borderColor: t.color.accent, backgroundColor: t.color.accent },
    chipTextOn: { color: t.color.onAccent },
    hover: { borderColor: t.color.text },
  }),
);

export function OrderingFor() {
  const styles = useStyles();
  const router = useRouter();
  const shopping = useShopping();
  const shopper = useShopper();
  if (shopping.mode === "onBehalf" && shopping.onBehalf) {
    return (
      <InlineMessage tone="info">{shopping.onBehalf.banner}</InlineMessage>
    );
  }
  const unlinked = shopper.all.filter((s) => !shopper.linked.includes(s));
  return (
    <View style={styles.box}>
      <Text size="sm" weight="semiBold" tone="muted">
        {SHOP_COPY.orderingFor}
      </Text>
      <View
        style={styles.chips}
        role="radiogroup"
        aria-label={SHOP_COPY.orderingFor}
      >
        {shopper.linked.map((s) => {
          const on = s.id === shopper.studentId;
          const label = `${s.name} · ${classSectionText(s.classLevel, s.section)}`;
          return (
            <PressableTarget
              key={s.id}
              accessibilityRole="radio"
              accessibilityLabel={label}
              aria-checked={on}
              accessibilityState={{ checked: on }}
              onPress={() => selectStudent(s.id)}
              style={on ? [styles.chip, styles.chipOn] : styles.chip}
              hoveredStyle={on ? undefined : styles.hover}
            >
              <Text
                weight="semiBold"
                style={on ? styles.chipTextOn : undefined}
              >
                {label}
              </Text>
            </PressableTarget>
          );
        })}
        {unlinked.map((s) => (
          <PressableTarget
            key={s.id}
            accessibilityRole="link"
            accessibilityLabel={`${s.name}, ${SHOP_COPY.notLinked}`}
            onPress={() => router.push(`/children/${s.id}`)}
            style={styles.chip}
            hoveredStyle={styles.hover}
          >
            <Text tone="muted">{`${s.name} · ${SHOP_COPY.notLinked}`}</Text>
          </PressableTarget>
        ))}
        {canAddChild(shopper.all.length) ? (
          <PressableTarget
            accessibilityRole="link"
            accessibilityLabel={SHOP_COPY.addStudent}
            onPress={() => router.push("/enter-school-code?mode=add")}
            style={styles.chip}
            hoveredStyle={styles.hover}
          >
            <Text weight="semiBold" tone="muted">
              {SHOP_COPY.addStudent}
            </Text>
          </PressableTarget>
        ) : null}
      </View>
    </View>
  );
}
